import React, { useMemo } from "react";
import { AlertTriangle, ArrowLeft, ExternalLink, ClipboardList } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Badge } from "../../../ui/badge";
import { Button } from "../../../ui/button";
import { getStakeholderLaneAppearance, LANE_STATUS_META } from "../../config/stakeholderLaneConfig";

const CANONICAL_LINKS = [
  { href: "https://github.com/DarbyEntLLC/dad-partners/blob/main/docs/roadmap.md", label: "Roadmap" },
  { href: "https://github.com/DarbyEntLLC/dad-partners/blob/main/docs/strategy-and-operations.md", label: "Strategy & Operations" },
];

function OperationCard({ item, onOpen }) {
  const appearance = getStakeholderLaneAppearance(item.lane);
  const disablePersonRouteActions = !!item.disable_person_route_actions;

  return (
    <button
      className={`w-full rounded-2xl border border-slate-200/80 bg-white/90 p-4 text-left shadow-sm transition hover:shadow-md dark:border-white/10 dark:bg-white/[0.04] ${appearance.cardHoverClass} ${disablePersonRouteActions ? "cursor-not-allowed opacity-70" : ""}`}
      disabled={disablePersonRouteActions}
      onClick={() => onOpen(item.id)}
      type="button"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.title}</p>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{item.reason}</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {item.is_kevin_flagged ? <Badge>{item.urgency_label || "Direct"}</Badge> : null}
          {item.type ? <Badge className={`border ${appearance.chipClass}`} variant="outline">{item.type}</Badge> : null}
        </div>
      </div>

      {item.next_move ? (
        <p className={`mt-3 text-xs font-medium uppercase tracking-[0.16em] ${appearance.accentTextClass}`}>
          Next: {item.next_move}
        </p>
      ) : null}
      {item.last_meaningful_movement ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.last_meaningful_movement}</p>
      ) : null}
      {item.latest_contact_summary ? (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{item.latest_contact_summary}</p>
      ) : null}
      {item.next_follow_up_summary ? (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{item.next_follow_up_summary}</p>
      ) : null}
    </button>
  );
}

function Section({ children, className = "", description, title }) {
  return (
    <section className={`rounded-[26px] border border-slate-200/80 bg-slate-50/85 p-5 dark:border-white/10 dark:bg-white/[0.03] ${className}`}>
      <div className="border-b border-slate-200/80 pb-4 dark:border-white/10">
        <h2 className="text-lg font-semibold text-slate-950 dark:text-white">{title}</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{description}</p>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

export default function OperationsLanePage({ lane, laneConfig, onOpen }) {
  const navigate = useNavigate();
  const statusMeta = LANE_STATUS_META[lane.status];
  const appearance = getStakeholderLaneAppearance(lane.id);

  const needsNowItems = useMemo(() => lane.items["needs-now"] || [], [lane.items]);
  const movingItems = useMemo(() => lane.items.moving || [], [lane.items]);
  const watchItems = useMemo(() => lane.items.watch || [], [lane.items]);
  const allItems = useMemo(() => [...needsNowItems, ...movingItems, ...watchItems], [needsNowItems, movingItems, watchItems]);

  const byType = useMemo(() => {
    return allItems.reduce((acc, item) => {
      const key = item.type || "general";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [allItems]);

  const signals = [
    {
      label: "Urgent operational issues",
      value: needsNowItems.length,
      note: "Items that could damage trust or clarity if delayed this week.",
    },
    {
      label: "Financial / support items",
      value: byType.financial || 0,
      note: "Money and support-readout items currently surfaced here.",
    },
    {
      label: "System / monitoring items",
      value: byType.system || 0,
      note: "Technical or monitoring truth requiring attention or confirmation.",
    },
    {
      label: "Reporting items",
      value: byType.reporting || 0,
      note: "Review-rhythm, report, and clarity-work still living in this lane.",
    },
  ];

  return (
    <div className={`min-h-screen pb-10 ${appearance.pageClass}`}>
      <div className="mx-auto max-w-[1440px] px-4 py-6 sm:px-6 lg:px-8">
        <div className={`rounded-[30px] border bg-white/85 p-6 shadow-[0_35px_80px_-38px_rgba(15,23,42,0.45)] backdrop-blur dark:bg-[#081018]/90 ${appearance.shellClass}`}>
          <div className="flex flex-col gap-5 border-b border-slate-200/80 pb-6 dark:border-white/10 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-4xl">
              <p className={`text-xs font-semibold uppercase tracking-[0.24em] ${appearance.headingTextClass}`}>
                Lane Detail
              </p>
              <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950 dark:text-white">
                {lane.title}
              </h1>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{lane.promise}</p>
              <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                {laneConfig.charter}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <Badge className={`border ${statusMeta.className}`}>{statusMeta.label}</Badge>
              <Button onClick={() => navigate("/community")} variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Community Home
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-3 xl:grid-cols-5">
            <div className="rounded-2xl border border-rose-200/70 bg-rose-50/80 p-5 dark:border-rose-400/20 dark:bg-rose-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-rose-700 dark:text-rose-100">
                Needs Now
              </p>
              <p className="mt-2 text-3xl font-semibold text-rose-950 dark:text-white">
                {lane.counts["needs-now"]}
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-200/70 bg-emerald-50/80 p-5 dark:border-emerald-400/20 dark:bg-emerald-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-100">
                Moving
              </p>
              <p className="mt-2 text-3xl font-semibold text-emerald-950 dark:text-white">
                {lane.counts.moving}
              </p>
            </div>
            <div className="rounded-2xl border border-amber-200/70 bg-amber-50/80 p-5 dark:border-amber-400/20 dark:bg-amber-500/10">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-100">
                Watch
              </p>
              <p className="mt-2 text-3xl font-semibold text-amber-950 dark:text-white">
                {lane.counts.watch}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/90 p-5 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                Trust-risk load
              </p>
              <p className="mt-2 inline-flex items-center gap-2 text-3xl font-semibold text-slate-950 dark:text-white">
                <AlertTriangle className={`h-6 w-6 ${appearance.accentTextClass}`} />
                {signals[0].value}
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200/80 bg-slate-50/90 p-5 dark:border-white/10 dark:bg-white/[0.04]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-600 dark:text-slate-300">
                Reporting load
              </p>
              <p className="mt-2 text-3xl font-semibold text-slate-950 dark:text-white">
                {signals[3].value}
              </p>
            </div>
          </div>

          <div className="mt-8 grid gap-6 xl:grid-cols-[1.4fr_0.9fr]">
            <div className="space-y-6">
              <Section
                className={appearance.panelClass}
                description="Operations is the immediate execution readout. Longer-lived product, strategy, idea, question, and decision work belongs in Admin → Operations → Features & Operations."
                title="Move durable planning to the queue"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <Button onClick={() => navigate("/")} type="button">
                    <ClipboardList className="mr-2 h-4 w-4" />
                    Open admin planning queue
                  </Button>
                  {CANONICAL_LINKS.map((link) => (
                    <a className="inline-flex items-center gap-1 text-sm font-medium text-cyan-800 hover:underline dark:text-aqua" href={link.href} key={link.href} rel="noopener noreferrer" target="_blank">
                      {link.label} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ))}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Operational items that could damage trust, delay delivery, or keep truth too fuzzy this week."
                title="Needs Kevin Now"
              >
                <div className="space-y-3">
                  {needsNowItems.length ? (
                    needsNowItems.map((item) => <OperationCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No urgent operational issues are surfaced right now.
                    </p>
                  )}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Operational truths that are moving in the right direction and should stay visible."
                title="Moving"
              >
                <div className="space-y-3">
                  {movingItems.length ? (
                    movingItems.map((item) => <OperationCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No positive operational movement is being tracked yet.
                    </p>
                  )}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Items that are not yet urgent, but still point to missing clarity or unfinished review rhythms."
                title="Watch"
              >
                <div className="space-y-3">
                  {watchItems.length ? (
                    watchItems.map((item) => <OperationCard item={item} key={item.id} onOpen={onOpen} />)
                  ) : (
                    <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-5 text-sm text-slate-500 dark:border-white/10 dark:text-slate-400">
                      No watch-level operational issues are surfaced right now.
                    </p>
                  )}
                </div>
              </Section>
            </div>

            <div className="space-y-6">
              <Section
                className={appearance.panelClass}
                description="A quick read on where this lane is spending its attention right now."
                title="Operational Signals"
              >
                <div className="space-y-3">
                  {signals.map((signal) => (
                    <div
                      className={`rounded-2xl border p-4 ${appearance.panelClass}`}
                      key={signal.label}
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="text-sm font-semibold text-slate-950 dark:text-white">{signal.label}</p>
                        <p className="text-2xl font-semibold text-slate-950 dark:text-white">{signal.value}</p>
                      </div>
                      <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{signal.note}</p>
                    </div>
                  ))}
                </div>
              </Section>

              <Section
                className={appearance.panelClass}
                description="Open any operations card to clarify notes, next move, and whether the item still needs you personally."
                title="Review Rhythms"
              >
                <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                  This lane should answer whether the business is clear enough, whether promises are being kept, and
                  whether any issue here could quietly erode trust if it stays fuzzy.
                </p>
              </Section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
